'use client';
import React, { useState, useRef, useEffect } from 'react';
import Modal from 'react-modal';
import styled from '@benzinga/themetron';
import Image from 'next/image';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { Icon } from '@benzinga/core-ui';
import Hooks from '@benzinga/hooks';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faChevronLeft as faLightChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight as faLightChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { GalleryCarouselItem } from './GalleryCarouselItem';
import { Thumb, ThumbsContainer } from './Thumb';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { Splide as ISplide } from '@splidejs/splide';

import styles from './GalleryCarousel.module.scss';

export const SplideStyle = styles;

export interface GallerySlideProps {
  id: string | number;
  title?: string;
  description?: string;
  caption?: string;
  image: string;
  thumbnail?: string;
}

export interface GalleryCarouselProps {
  className?: string;
  items: GallerySlideProps[];
  showThumbs?: boolean;
  disableLightbox?: boolean;
  onItemChange?: (item: GallerySlideProps) => void;
}

const customStyles = {
  content: {
    background: 'rgba(0,0,0,.92)',
    border: 'none',
    borderRadius: '0',
    bottom: 'auto',
    height: '100%',
    left: '50%',
    marginRight: '-50%',
    padding: '0',
    right: 'auto',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
  },
  overlay: {
    backdropFilter: 'blur(4px)',
    inset: '-1px',
    zIndex: 50,
  },
};

export const GalleryCarousel: React.FC<GalleryCarouselProps> = ({
  className = '',
  disableLightbox,
  items,
  showThumbs = false,
}) => {
  const carouselRef = useRef<Splide>(null);
  const thumbsRef = useRef<Splide>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(items ? items[0] : null);
  const [activeItemLightbox, setActiveItemLightbox] = useState(activeItem);

  Hooks.useBodyLock(isLightboxOpen);

  useEffect(() => {
    if (carouselRef.current && thumbsRef.current && thumbsRef.current.splide) {
      carouselRef.current.sync(thumbsRef.current.splide);
    }
  }, []);

  const areThumbsShown = showThumbs && items.length > 1;

  if (!items || !activeItemLightbox) return null;

  const lightboxImage = activeItemLightbox?.image;
  const currentItemIndex = items.indexOf(activeItemLightbox);
  const lastItemIndex = items.length - 1;
  const variant = items.length === 1 ? 'single' : 'multiple';
  const containerClassname = `gallery-carousel ${styles.splide_slidewrap} ${variant} ${className}`.trim();

  const handleToggleLightbox = () => {
    if (disableLightbox) return;
    setActiveItemLightbox(activeItem);
    setIsLightboxOpen(!isLightboxOpen);
  };

  const handleCloseLightbox = () => setIsLightboxOpen(false);

  const handlePrevLightboxItem = () => {
    if (currentItemIndex === 0) return;
    const prevItem = items[currentItemIndex - 1];
    setActiveItemLightbox(prevItem);
  };

  const handleNextLightboxItem = () => {
    if (lastItemIndex === currentItemIndex) return;
    const nextItem = items[currentItemIndex + 1];
    setActiveItemLightbox(nextItem);
  };

  const onMove = (_splide: ISplide, index: number) => setActiveItem(items[index]);

  return (
    <>
      <Modal
        contentLabel="Gallery Carousel Lightbox"
        isOpen={isLightboxOpen}
        onRequestClose={handleCloseLightbox}
        style={customStyles}
      >
        <ModalContent className="modal-content">
          <StyledButtons className="close-button" onClick={handleCloseLightbox}>
            <Icon icon={faTimes} />
          </StyledButtons>
          <StyledButtons
            className="chevron-left-button"
            disabled={currentItemIndex === 0}
            onClick={handlePrevLightboxItem}
          >
            <Icon className="chevron-left" icon={faLightChevronLeft} />
          </StyledButtons>
          <div className="current-image-wrapper">
            <Image
              alt=""
              fill
              sizes="100vw"
              src={lightboxImage}
              style={{ objectFit: 'contain' }}
            />
          </div>
          <StyledButtons
            className="chevron-right-button"
            disabled={lastItemIndex === currentItemIndex}
            onClick={handleNextLightboxItem}
          >
            <Icon icon={faLightChevronRight} />
          </StyledButtons>
        </ModalContent>
      </Modal>
      <Container className={containerClassname}>
        <div className={`carousel-wrapper ${disableLightbox ? 'no-lightbox' : 'lightbox'}`}>
          <Splide
            aria-label="Gallery Carousel"
            hasTrack={false}
            onMove={onMove}
            options={{
              arrows: items.length > 1,
              pagination: false,
            }}
            ref={carouselRef}
          >
            <SplideTrack>
              {items.map((slide, index) => {
                return (
                  <SplideSlide key={`gallery-carousel-item-${slide?.id}-${index}`} onClick={handleToggleLightbox}>
                    <GalleryCarouselItem {...slide} />
                  </SplideSlide>
                );
              })}
            </SplideTrack>
          </Splide>
          {activeItem && (
            <div className="gallery-carousel-item-info">
              {activeItem.title && <h6 className="gallery-carousel-title">{activeItem.title}</h6>}
              {activeItem.description && (
                <div
                  className="gallery-carousel-description"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(activeItem.description) }}
                />
              )}
              {activeItem.caption && <caption className="gallery-carousel-caption">{activeItem.caption}</caption>}
            </div>
          )}
        </div>

        {areThumbsShown && (
          <ThumbsContainer className="thumbs-container">
            <Splide
              aria-label="Thumbnail Carousel"
              options={{
                arrows: false,
                cover: true,
                fixedHeight: 70,
                fixedWidth: 110,
                focus: 'center',
                gap: '1rem',
                isNavigation: true,
                pagination: false,
                rewind: true,
                type: 'slide',
              }}
              ref={thumbsRef}
            >
              {items.map((slide, index) => {
                const image = slide.image;
                return (
                  <SplideSlide key={`gallery-carousel-item-${slide?.id}-${index}`}>
                    <Thumb>
                      <Image
                        alt=""
                        fill
                        sizes="100vw"
                        src={image}
                        style={{ objectFit: 'cover' }}
                      />
                    </Thumb>
                  </SplideSlide>
                );
              })}
            </Splide>
          </ThumbsContainer>
        )}
      </Container>
    </>
  );
};

const StyledButtons = styled.button`
  font-weight: 200;
  font-size: 2.5rem;
  cursor: pointer;

  &.close-button {
    display: flex;
    position: absolute;
    top: 0.3rem;
    right: 0.3rem;
    z-index: 3;
    color: #808080;
  }

  &.chevron-left-button,
  &.chevron-right-button {
    position: absolute;
    color: #ffffff;
    height: 100%;
    z-index: 2;

    svg {
      fill: #ffffff;
      filter: drop-shadow(1px 0 0 #000000) drop-shadow(-1px 0 0 #000000) drop-shadow(0 1px 0 #000000)
        drop-shadow(0 -1px 0 #000000) drop-shadow(1px 0 0 #000000) drop-shadow(-1px 0 0 #000000)
        drop-shadow(0 -1px 0 #000000) drop-shadow(0 1px 0 #000000);
    }
    &:disabled {
      color: #808080;
      svg {
        filter: none;
      }
    }
  }

  &.chevron-left-button {
    left: 0.3rem;
  }

  &.chevron-right-button {
    right: 0.3rem;
  }

  @media screen and (min-width: 768px) {
    &.chevron-left-button,
    &.chevron-right-button {
      height: auto;
      display: flex;
      position: unset;

      svg {
        color: #ffffff;
        filter: none;
      }

      &:disabled {
        cursor: not-allowed;
        svg {
          color: #808080;
          opacity: 0.7;
        }
      }
    }
  }
`;

const ModalContent = styled.div`
  &.modal-content {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;

    .current-image-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
    }

    @media screen and (min-width: 768px) {
      justify-content: space-between;
      .current-image-wrapper {
        max-width: 1200px;
      }
    }
  }
`;

const Container = styled.div`
  &.gallery-carousel {
    height: 100%;

    &.single {
      .gallery-carousel-item {
        padding: 0 20px;
      }
    }

    .carousel-wrapper {
      width: 100%;

      &.no-lightbox {
        .thumbnail {
          cursor: unset;
        }
      }

      .gallery-carousel-item-info {
        max-height: 90px;
        width: 100%;
        padding: 15px;
        background: #e6e6e6;
        border: 1px solid #d6d6d6;
        color: ${({ theme }) => theme.colorPalette.black};
        display: flex;
        flex-direction: column;
        justify-content: center;

        .gallery-carousel-title {
          font-weight: ${({ theme }) => theme.fontWeight.semibold};
          font-size: ${({ theme }) => theme.fontSize.base};
        }

        .gallery-carousel-caption {
          font-size: ${({ theme }) => theme.fontSize.sm};
          margin-bottom: 0;
          text-align: left;
        }

        .gallery-carousel-description {
          font-size: ${({ theme }) => theme.fontSize.base};
        }
      }

      .splide__arrow {
        align-items: center;
        background: #dfd9db;
        border: 0;
        border-radius: 14px;
        cursor: pointer;
        display: flex;
        height: 2em;
        width: 2em;
        justify-content: center;
        opacity: 0.7;
        padding: 2rem 0.5rem;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;

        &:disabled {
          opacity: 0.3;
        }
      }

      .splide__arrow--prev {
        left: 0.4rem;

        svg {
          transform: scaleX(-1);
        }
      }

      .splide__arrow--next {
        right: 0.4rem;
      }
    }
  }
`;
